/**
 * Polling Health Panel — v2.9.101 (2026-02-XX)
 * ─────────────────────────────────────────────────────────────────────────────
 * Collapsible widget that surfaces every Live-Audience station whose listener
 * polling cannot produce reliable retention / early-drop / LSI metrics.
 *
 * Data source:
 *   /api/admin/diagnostic/polls-per-song-average (via parent's
 *   pollingHealthMap) — required samples are now derived from each
 *   station's CONFIGURED poll_interval_seconds (10 / 15 / 20 / 30s)
 *   instead of a flat 30s baseline. This means health=1.0 reflects
 *   the worker meeting the operator's chosen cadence, not a global
 *   default.
 *
 * Per row:
 *   • Station name + verdict (✅ / 🟢 / 🟡 / 🟠 / 🔴)
 *   • Health ratio (avg actual polls / avg required polls)
 *   • Configured interval vs measured "effective" interval (drift)
 *   • Primary failure mode badge (no_listener_url, listener_endpoint_down,
 *     stream_mode_inferred, worker_drift, endpoint_intermittent)
 *   • Plays-with-zero-listener-data %, fail-open %, inferred %
 *   • Quick "Διόρθωση" button → onEditStation()
 *
 * IMPORTANT: Stream-Analytics stations (`data_mode === 'stream'`) are
 * filtered out at the parent (index.js) AND here as a safety net,
 * because they don't poll an external listener API and any "miss%"
 * metric would be misleading.
 */

import React, { useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Wrench, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';

// ─────────────────────────────────────────────────────────────────────────────
// Diagnosis helper — uses the backend's primary_failure_mode classifier
// (v2.9.101) when available, then falls back to per-row signals. Output
// includes a short label + a tone + an optional hint sentence the
// operator can act on (e.g. "configured 15s but measured 58s").
// ─────────────────────────────────────────────────────────────────────────────
const _failureLabels = {
  no_listener_url: {
    label: '🔴 Λείπει listener URL — δεν φτάνει ποτέ στο endpoint',
    tone: 'red',
  },
  listener_endpoint_down: {
    label: '🔴 Endpoint listeners πέφτει συνεχώς (fail-open)',
    tone: 'red',
  },
  listener_endpoint_returns_zero: {
    label: '🔴 URL configured αλλά επιστρέφει πάντα 0 — έλεγξε τον σωστό path',
    tone: 'red',
  },
  stream_mode_no_polling: {
    label: '🟣 Stream Analytics mode — δεν polls listeners (κανονικό)',
    tone: 'amber',
  },
  stream_mode_inferred: {
    label: '🟠 Stream-mode: μόνο start/end samples — δεν παράγει retention curve',
    tone: 'amber',
  },
  worker_drift: {
    label: '🟠 Worker drift — παράγει λιγότερα samples από το configured',
    tone: 'amber',
  },
  endpoint_intermittent: {
    label: '🟠 Endpoint κατά διαστήματα αποτυγχάνει',
    tone: 'amber',
  },
};

const _diagnose = (h) => {
  if (!h) return { label: 'Χωρίς δεδομένα', tone: 'gray' };

  const health = h.health_ratio ?? 0;
  // v2.9.167 — analytics reliability is the metric to lead with:
  // "are my retention/LSI numbers trustworthy?". cadence health is
  // shown as supporting context for worker-drift diagnostics.
  const reliability = h.analytics_reliability ?? null;
  const reliabilityVerdict = h.analytics_reliability_verdict ?? null;
  const zeroPct = h.plays_with_zero_samples_pct ?? 0;
  const failOpenPct = h.fail_open_plays_pct ?? 0;
  const inferredPct = h.inferred_plays_pct ?? 0;
  const avgActual = h.avg_samples ?? h.avg_samples_per_play ?? 0;
  const avgRequired = h.avg_required_samples ?? 0;
  const target = h.poll_interval_seconds ?? h.poll_interval_seconds_used ?? 30;
  const derived = h.derived_actual_interval_seconds;
  const drift = h.interval_drift_ratio;
  const failureMode = h.primary_failure_mode;

  // v2.9.167 — Lead with analytics reliability when the backend
  // exposes it. The cadence health is still surfaced as supporting
  // hint text so the operator can still tell a healthy-but-slow
  // worker apart from a worker-drift problem.
  if (reliability !== null) {
    if (reliability >= 0.95) {
      return {
        label: '✅ Υγιές — πλήρη retention/LSI δεδομένα',
        tone: 'green',
        hint: drift && drift > 1.5
          ? `Configured ${target}s, measured ~${derived}s (drift ${drift?.toFixed(1)}×) — η αξιοπιστία παραμένει 100% επειδή τα ${avgActual.toFixed(1)} samples καλύπτουν την ανάλυση.`
          : derived ? `Configured ${target}s, measured ~${derived}s.` : null,
      };
    }
    if (reliability >= 0.7) {
      return {
        label: '🟢 Καλό — αξιόπιστα retention/LSI',
        tone: 'green',
        hint: `Μ.ο. ${avgActual.toFixed(1)} samples ανά τραγούδι. Reliability ${(reliability*100).toFixed(0)}%.`,
      };
    }
  }

  if (health >= 1.0 && reliability === null) {
    return {
      label: '✅ Υγιές — πλήρη retention/LSI δεδομένα',
      tone: 'green',
      hint: derived ? `Configured ${target}s, measured ~${derived}s.` : null,
    };
  }
  if (health >= 0.7 && reliability === null) {
    return {
      label: '🟢 Καλό — αξιόπιστα retention/LSI',
      tone: 'green',
      hint: `Μ.ο. ${avgActual.toFixed(1)} polls (στόχος ${avgRequired.toFixed(1)}). ${
        derived ? `Configured ${target}s, measured ~${derived}s.` : ''
      }`,
    };
  }

  // Below the "good" thresholds — fall through to backend-provided
  // failure-mode taxonomy. NOTE: a station with worker_drift but
  // reliability ≥ 0.95 was already handled above — that's the very
  // case where we used to scream at the user unnecessarily.
  if (failureMode && _failureLabels[failureMode]) {
    const base = _failureLabels[failureMode];
    let hint = `Μ.ο. ${avgActual.toFixed(1)} polls (στόχος ${avgRequired.toFixed(1)}).`;
    if (failureMode === 'no_listener_url') {
      hint = `${zeroPct.toFixed(0)}% των plays χωρίς listener data. Πρόσθεσε listeners_api_url ή stats_url.`;
    } else if (failureMode === 'listener_endpoint_returns_zero') {
      hint = `Το endpoint απαντά κανονικά αλλά γυρίζει πάντα 0 listeners. Δοκίμασε άλλο path (π.χ. /stats?json=1 αντί /7.html).`;
    } else if (failureMode === 'listener_endpoint_down') {
      hint = `${failOpenPct.toFixed(0)}% fail-open plays. Το endpoint απαντά αλλά με σφάλμα ή 0. Έλεγξε το URL.`;
    } else if (failureMode === 'stream_mode_inferred') {
      hint = `${inferredPct.toFixed(0)}% inferred plays — γύρισε σε live-poll mode για retention/LSI.`;
    } else if (failureMode === 'stream_mode_no_polling') {
      hint = `Stream Analytics: ο σταθμός κατάγραφει plays με start/end synthetic samples (κανονικό). Δεν απαιτεί διόρθωση.`;
    } else if (failureMode === 'worker_drift' && derived) {
      hint = `Configured ${target}s αλλά παράγει ~${derived}s effective (drift ${drift?.toFixed(1)}×). Worker overload ή slow endpoint.`;
    } else if (failureMode === 'endpoint_intermittent' && derived) {
      hint = `Configured ${target}s, measured ~${derived}s. Endpoint timeouts ή race με song-change.`;
    }
    return { ...base, hint };
  }

  // Generic fallback for low reliability when backend didn't classify
  if ((reliability ?? health) >= 0.4) {
    return {
      label: '🟡 Οριακό — retention/LSI υπολογίζονται αλλά με μικρό sample',
      tone: 'amber',
      hint: derived
        ? `Configured ${target}s, measured ~${derived}s. Μ.ο. ${avgActual.toFixed(1)} samples (στόχος για 100% reliability: 6+ ανά τραγούδι).`
        : `Μ.ο. ${avgActual.toFixed(1)} samples (στόχος για 100% reliability: 6+ ανά τραγούδι).`,
    };
  }
  if ((reliability ?? health) >= 0.1) {
    return {
      label: '🟠 Φτωχό — οι μετρικές δεν είναι αξιόπιστες',
      tone: 'amber',
      hint: derived
        ? `Configured ${target}s, measured ~${derived}s. ${zeroPct.toFixed(0)}% plays χωρίς δεδομένα.`
        : `Μ.ο. ${avgActual.toFixed(1)} polls. ${zeroPct.toFixed(0)}% plays χωρίς δεδομένα.`,
    };
  }
  return {
    label: '🔴 Κρίσιμο — σχεδόν κανένα retention/LSI δεν είναι έγκυρο',
    tone: 'red',
    hint: derived
      ? `Configured ${target}s αλλά measured ~${derived}s. ${zeroPct.toFixed(0)}% plays χωρίς listener data.`
      : `${zeroPct.toFixed(0)}% plays χωρίς listener data. URL πιθανότατα down ή λείπει.`,
  };
};

const _statusText = (status, count) => {
  const at = status?.updatedAt
    ? new Date(status.updatedAt).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })
    : null;
  if (status?.state === 'loading') return 'Φόρτωση…';
  if (status?.state === 'computing') return 'Υπολογισμός στον server (έως ~2′)…';
  if (status?.state === 'error') return at ? `Δεν ανανεώθηκε — τελευταία ενημέρωση ${at}` : 'Δεν φορτώθηκαν τα δεδομένα — νέα προσπάθεια σε 5′';
  if (!count) return `Όλοι οι σταθμοί αξιόπιστοι · ενημέρωση ${at || '—'}`;
  return `health < 0.7 σε 24h — Stream Analytics εξαιρούνται · ενημέρωση ${at || '—'}`;
};

const TroubleStationsPanel = ({ stations, pollingHealthMap, pollingHealthMeta, pollingHealthStatus, onEditStation, onRevertAutoTune }) => {
  const [expanded, setExpanded] = useState(false);
  const [sortKey, setSortKey] = useState('health'); // health | name

  // Build the list — every Live-Audience station whose health < 0.7
  // (i.e. anything worse than "🟢 good"). Stream-Analytics stations
  // are already excluded by the parent's fetchPollingHealth.
  const trouble = useMemo(() => {
    if (!stations || !pollingHealthMap) return [];
    const out = [];
    for (const s of stations) {
      const h = pollingHealthMap[s._id];
      if (!h) continue;
      // Safety net — also skip stream-mode here in case the parent
      // ever sends them through.
      if ((s.data_mode || 'stream') === 'stream') continue;
      if ((h.total_plays ?? 0) === 0) continue;
      // v2.9.167 — include the new reliability fields and use them
      // as the primary "is this station worth flagging?" gate. Worker
      // drift alone (cadence < 0.7 but reliability ≥ 0.95) is no
      // longer enough to put a station in the trouble panel — its
      // analytics are still trustworthy.
      const reliability = h.analytics_reliability ?? null;
      const cadence = h.health_ratio ?? 0;
      // Threshold: reliability < 0.7 when available, else fall back
      // to the legacy cadence threshold.
      const gateFails = reliability !== null
        ? reliability < 0.7
        : cadence < 0.7;
      if (!gateFails) continue;
      out.push({
        id: s._id,
        name: s.name || s._id,
        health: h.health_ratio ?? 0,
        reliability: reliability,
        reliabilityVerdict: h.analytics_reliability_verdict ?? null,
        verdict: h.verdict,
        reliablePct: h.plays_with_reliable_metrics_pct ?? 0,
        zeroPct: h.plays_with_zero_samples_pct ?? 0,
        failOpenPct: h.fail_open_plays_pct ?? 0,
        inferredPct: h.inferred_plays_pct ?? 0,
        plays: h.total_plays,
        avgActual: h.avg_samples ?? h.avg_samples_per_play ?? 0,
        avgRequired: h.avg_required_samples ?? 0,
        pollInterval: h.poll_interval_seconds ?? h.poll_interval_seconds_used,
        derivedInterval: h.derived_actual_interval_seconds,
        driftRatio: h.interval_drift_ratio,
        failureMode: h.primary_failure_mode,
        // v2.9.103 — auto-tune metadata for badge + revert UI
        autoTuned: h.poll_interval_auto_tuned ?? false,
        autoTunedAt: h.poll_interval_auto_tuned_at ?? null,
        autoTunedFrom: h.poll_interval_auto_tuned_from ?? null,
        isHighPriority: h.is_high_priority ?? false,
        diag: _diagnose(h),
        raw: h,
        station: s,
      });
    }
    return out;
  }, [stations, pollingHealthMap]);

  const sorted = useMemo(() => {
    const list = [...trouble];
    if (sortKey === 'name') {
      list.sort((a, b) => a.name.localeCompare(b.name, 'el'));
    } else {
      list.sort((a, b) => a.health - b.health); // worst first
    }
    return list;
  }, [trouble, sortKey]);

  // Always rendered (2026-11-01): loading / computing / error / all-reliable states included.
  return (
    <div
      data-testid="trouble-stations-panel"
      className="rounded-lg border border-amber-300/40 dark:border-amber-700/40 bg-amber-50/60 dark:bg-amber-900/15 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-amber-100/40 dark:hover:bg-amber-900/25 transition-colors"
        data-testid="trouble-stations-toggle"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
            Αξιοπιστία Retention / Early Drop / LSI
          </span>
          <span data-testid="trouble-stations-count" className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-amber-200/70 dark:bg-amber-800/50 text-amber-900 dark:text-amber-100">
            {pollingHealthStatus?.state === 'loading' || pollingHealthStatus?.state === 'computing' ? '…' : trouble.length}
          </span>
          <span data-testid="trouble-stations-status" className="text-xs text-amber-700/70 dark:text-amber-300/70">
            {_statusText(pollingHealthStatus, trouble.length)}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-amber-700 dark:text-amber-300" />
        ) : (
          <ChevronDown className="w-4 h-4 text-amber-700 dark:text-amber-300" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-amber-300/40 dark:border-amber-700/40">
          {/* Polling model + reliability explainer */}
          <div className="px-3 py-1.5 text-[11px] bg-blue-100/50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-200 border-b border-blue-200/40 leading-relaxed">
            📐 <strong>Δύο ξεχωριστές μετρικές (v2.9.167):</strong>
            <br />
            <span className="font-medium">Health</span> = συμμορφώθηκε ο worker με το configured interval; (cadence tuning)
            <br />
            <span className="font-medium">Αξιοπιστία retention/LSI</span> = έχει ≥1 sample κάθε 30s για κάθε τραγούδι; (data trustworthiness)
            <br />
            <span className="opacity-80">
              180s τραγούδι → 6 samples = 100% αξιοπιστία. 360s remix → 12. 600s stuck → 20. Ανεξάρτητο από configured interval.
            </span>
          </div>
          {pollingHealthMeta && pollingHealthMeta.build
            && !pollingHealthMeta.build.match(/^v2\.9\.(10[1-9]|1[1-9][0-9]|[2-9][0-9][0-9])/) && (
            <div className="px-3 py-1.5 text-[11px] bg-red-100/70 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-b border-red-300/40">
              ⚠️ Το backend τρέχει build <strong>{pollingHealthMeta.build}</strong>.
              Το per-station interval-aware health εμφανίστηκε στο v2.9.101. Κάνε redeploy.
            </div>
          )}

          {/* Sort controls */}
          <div className="px-3 py-1.5 flex items-center gap-2 text-xs text-amber-800 dark:text-amber-200 bg-amber-100/40 dark:bg-amber-900/20">
            <span>Ταξινόμηση:</span>
            <button
              type="button"
              onClick={() => setSortKey('health')}
              className={`px-2 py-0.5 rounded ${
                sortKey === 'health'
                  ? 'bg-amber-300/70 dark:bg-amber-700/60 font-semibold'
                  : 'hover:bg-amber-200/40 dark:hover:bg-amber-800/40'
              }`}
              data-testid="sort-by-health"
            >
              Health %
            </button>
            <button
              type="button"
              onClick={() => setSortKey('name')}
              className={`px-2 py-0.5 rounded ${
                sortKey === 'name'
                  ? 'bg-amber-300/70 dark:bg-amber-700/60 font-semibold'
                  : 'hover:bg-amber-200/40 dark:hover:bg-amber-800/40'
              }`}
              data-testid="sort-by-name"
            >
              Όνομα
            </button>
          </div>

          {/* Rows */}
          <div className="divide-y divide-amber-200/50 dark:divide-amber-800/40">
            {sorted.length === 0 && (
              <div data-testid="trouble-stations-empty" className="px-3 py-3 text-xs text-amber-800 dark:text-amber-200">
                {_statusText(pollingHealthStatus, 0)}
              </div>
            )}
            {sorted.map((row) => {
              const toneText =
                row.diag.tone === 'red'
                  ? 'text-red-700 dark:text-red-300'
                  : row.diag.tone === 'amber'
                  ? 'text-amber-700 dark:text-amber-300'
                  : 'text-green-700 dark:text-green-300';
              const healthBadgeColor =
                row.health >= 0.4
                  ? 'bg-amber-200/70 dark:bg-amber-800/60 text-amber-900 dark:text-amber-100'
                  : row.health >= 0.1
                  ? 'bg-orange-200/70 dark:bg-orange-800/60 text-orange-900 dark:text-orange-100'
                  : 'bg-red-200/70 dark:bg-red-800/60 text-red-900 dark:text-red-100';
              return (
                <div
                  key={row.id}
                  className="px-3 py-2 flex items-center justify-between gap-3 hover:bg-amber-100/30 dark:hover:bg-amber-900/20"
                  data-testid={`trouble-row-${row.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-sm font-medium text-gray-900 dark:text-white truncate"
                        title={row.name}
                      >
                        {row.name}
                      </span>
                      <span
                        className={`text-xs font-semibold px-1.5 py-0.5 rounded ${healthBadgeColor}`}
                        title="Health ratio: μ.ο. πραγματικών polls / μ.ο. απαιτούμενων polls στο configured interval του σταθμού. 1.0 = ο worker τηρεί το ρυθμισμένο cadence."
                      >
                        Health {Math.round(row.health * 100)}%
                      </span>
                      <span
                        className="text-[10px] text-gray-600 dark:text-gray-400"
                        title="Configured poll interval (από τις ρυθμίσεις του σταθμού)."
                      >
                        @ {row.pollInterval || 30}s configured
                      </span>
                      {row.derivedInterval != null && (
                        <span
                          className={`text-[10px] px-1 rounded ${
                            row.driftRatio && row.driftRatio >= 1.6
                              ? 'bg-red-200/70 dark:bg-red-800/60 text-red-900 dark:text-red-100 font-semibold'
                              : row.driftRatio && row.driftRatio >= 1.2
                              ? 'bg-amber-200/70 dark:bg-amber-800/60 text-amber-900 dark:text-amber-100'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}
                          title="Effective interval = avg(duration) / avg(samples_per_play). Πόσα δευτερόλεπτα μεσολαβούν, στην πράξη, μεταξύ διαδοχικών μετρήσεων. Αν διαφέρει >60% από το configured, ο worker έχει drift."
                        >
                          ≈ {row.derivedInterval}s measured
                          {row.driftRatio ? ` (${row.driftRatio.toFixed(1)}×)` : ''}
                        </span>
                      )}
                      {/* v2.9.103 — Auto-tune badge */}
                      {row.autoTuned && (
                        <span
                          data-testid={`auto-tune-badge-${row.id}`}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-200/80 dark:bg-indigo-800/70 text-indigo-900 dark:text-indigo-100 font-medium"
                          title={`Ο nightly auto-tuner ανέβασε το poll interval από ${row.autoTunedFrom}s σε ${row.pollInterval}s στις ${row.autoTunedAt ? new Date(row.autoTunedAt).toLocaleString('el-GR') : '—'} επειδή ο drift είχε ξεπεράσει το 3× για > 24h. Μπορείς να κάνεις επαναφορά αν δεν θέλεις την αυτόματη αλλαγή.`}
                        >
                          🤖 Auto-tuned {row.autoTunedFrom}s → {row.pollInterval}s
                        </span>
                      )}
                      {/* v2.9.103 — High-priority badge */}
                      {row.isHighPriority && (
                        <span
                          data-testid={`hi-priority-badge-${row.id}`}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-purple-200/70 dark:bg-purple-800/60 text-purple-900 dark:text-purple-100 font-medium"
                          title="High-priority station: ο auto-tuner ΔΕΝ θα αλλάξει αυτόματα το interval του. Πρέπει να διορθώσεις τον slow endpoint manually ή να αφαιρέσεις το high-priority flag."
                        >
                          ⭐ High priority
                        </span>
                      )}
                    </div>

                    {/* Reliability metrics */}
                    <div className="mt-0.5 text-[11px] text-gray-600 dark:text-gray-300 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                      <span title="Πραγματικός μ.ο. polls ανά τραγούδι έναντι απαιτούμενου στο configured interval">
                        📊 {row.avgActual.toFixed(1)} / {row.avgRequired.toFixed(1)} polls
                      </span>
                      {/* v2.9.167 — Analytics reliability badge.
                          Continuous 0-1 score (averaged across plays);
                          duration-aware so long/stuck songs auto-surface
                          as low even with normal raw counts. */}
                      {row.reliability !== null && row.reliability !== undefined && (
                        <span
                          data-testid={`analytics-reliability-${row.id}`}
                          className={
                            row.reliability >= 0.95
                              ? 'text-green-700 dark:text-green-300 font-medium'
                              : row.reliability >= 0.7
                              ? 'text-green-700 dark:text-green-300'
                              : row.reliability >= 0.4
                              ? 'text-amber-700 dark:text-amber-300'
                              : 'text-red-700 dark:text-red-300'
                          }
                          title={
                            "Αξιοπιστία retention / Early-Drop / LSI (v2.9.167).\n\n" +
                            "Φόρμουλα: required = max(2, ceil(duration / 30s)). Δηλαδή 1 sample κάθε 30s.\n\n" +
                            "• 180s τραγούδι → 6 samples για 100%\n" +
                            "• 360s remix → 12 samples για 100%\n" +
                            "• 600s stuck → 20 samples για 100%\n\n" +
                            "ΑΝΕΞΑΡΤΗΤΟ από το configured polling interval — ένας σταθμός που polls @10s αλλά παράγει effective ~25s εξακολουθεί να έχει 100% reliability αν έχει ≥6 samples στα τραγούδια του."
                          }
                        >
                          {row.reliability >= 0.95 ? '✅' : row.reliability >= 0.7 ? '🟢' : row.reliability >= 0.4 ? '🟡' : '🔴'}{' '}
                          {Math.round(row.reliability * 100)}% αξιοπιστία retention/LSI
                        </span>
                      )}
                      {/* Legacy "≥3 samples" badge — kept as a secondary
                          signal because it answers a slightly different
                          question (binary threshold per play). */}
                      {row.reliablePct > 0 && (row.reliability === null || row.reliability === undefined) && (
                        <span
                          className={
                            row.reliablePct >= 50
                              ? 'text-green-700 dark:text-green-300'
                              : 'text-amber-700 dark:text-amber-300'
                          }
                          title="Ποσοστό τραγουδιών με ≥3 samples — απαραίτητο όριο για αξιόπιστη retention/LSI καμπύλη."
                        >
                          ✅ {row.reliablePct.toFixed(0)}% αξιόπιστα retention/LSI
                        </span>
                      )}
                      {row.zeroPct > 0 && (
                        <span
                          className="text-red-700 dark:text-red-300"
                          title="Plays χωρίς listener data (no_listener_data:true). Δεν χάνονται από τη βάση — απλώς δεν παράγουν retention/LSI metrics."
                        >
                          📭 {row.zeroPct.toFixed(0)}% χωρίς δεδομένα
                        </span>
                      )}
                      {row.failOpenPct > 0 && (
                        <span
                          className="text-orange-700 dark:text-orange-300"
                          title="Plays όπου το listener endpoint αποτυγχάνει και ο worker γράφει [0] ως safety net (fail-open). Δείχνει spam από broken URL."
                        >
                          ⚠️ {row.failOpenPct.toFixed(0)}% fail-open
                        </span>
                      )}
                      {row.inferredPct > 0 && (
                        <span
                          className="text-purple-700 dark:text-purple-300"
                          title="Inferred plays (από song-change split). Συνήθως μόνο start/end samples — δεν παράγουν retention curve."
                        >
                          🧩 {row.inferredPct.toFixed(0)}% inferred
                        </span>
                      )}
                      <span className="text-gray-500" title="Πλήθος τραγουδιών στο παράθυρο">
                        🎵 {row.plays} plays
                      </span>
                    </div>

                    <div className={`mt-0.5 text-xs ${toneText}`}>{row.diag.label}</div>
                    {row.diag.hint && (
                      <div
                        className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 italic"
                        title="Πιο αναλυτική εξήγηση"
                      >
                        {row.diag.hint}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onEditStation && onEditStation(row.station)}
                      data-testid={`trouble-edit-${row.id}`}
                    >
                      <Wrench className="w-3.5 h-3.5 mr-1" />
                      Διόρθωση
                    </Button>
                    {row.autoTuned && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-[10px] h-7 px-2 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
                        onClick={() => onRevertAutoTune && onRevertAutoTune(row)}
                        data-testid={`trouble-revert-auto-tune-${row.id}`}
                        title={`Επαναφορά poll interval από ${row.pollInterval}s πίσω στα ${row.autoTunedFrom}s.`}
                      >
                        ↩ Επαναφορά {row.autoTunedFrom}s
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer hint */}
          <div className="px-3 py-2 text-[11px] text-amber-700/80 dark:text-amber-300/80 bg-amber-100/30 dark:bg-amber-900/20 border-t border-amber-200/50 dark:border-amber-800/40 flex items-center gap-2 flex-wrap">
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
            <span>
              Health = πραγματικά polls / απαιτούμενα. ≥3 samples χρειάζονται για
              αξιόπιστο retention. Stream Analytics εξαιρούνται γιατί δεν πολλάρουν.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TroubleStationsPanel;
